import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Spin, Row, Col, Tag, Avatar, Divider, Alert, Radio, Result } from 'antd';
import { InstagramOutlined, UserAddOutlined, CheckCircleOutlined, LinkOutlined, UserOutlined, TeamOutlined, PictureOutlined } from '@ant-design/icons';
import apiClient from '../../server/server';

const AIQuickProfileCreator = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [createdProfile, setCreatedProfile] = useState(null);
  const [error, setError] = useState(null);

  // Get user ID from localStorage
  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?._id || null;
  };

  // Extract Instagram handle from URL or handle
  const extractInstagramHandle = (input) => {
    if (!input) return null;
    // Remove @ if present
    let handle = input.trim().replace(/^@/, '');
    // Extract from URL if it's a URL
    const urlMatch = handle.match(/instagram\.com\/([^/?]+)/i);
    if (urlMatch) {
      handle = urlMatch[1];
    }
    return handle;
  };

  // Create Miami profile using the new endpoint
  const handleCreateProfile = async (values) => {
    const instagramHandle = extractInstagramHandle(values.instagramHandle);
    if (!instagramHandle) {
      message.warning('Please enter an Instagram handle or URL');
      return;
    }

    const userId = getUserId();
    if (!userId) {
      message.error('User ID not found. Please log in again.');
      return;
    }

    setLoading(true);
    setCreatedProfile(null);
    setError(null);

    try {
      const requestBody = {
        instagramHandle: instagramHandle,
        profileType: values.profileType,
        userId: userId
      };

      // Add optional overrides if provided
      if (values.name?.trim()) {
        requestBody.name = values.name.trim();
      }
      if (values.description?.trim()) {
        requestBody.description = values.description.trim();
      }
      // Add picture URL if provided (skips Instagram scraping)
      if (values.pictureUrl?.trim()) {
        requestBody.pictureUrl = values.pictureUrl.trim();
      }

      console.log('Creating Miami profile:', requestBody);

      const response = await apiClient.post('/ai/create-miami-profile', requestBody);

      if (response.data?.success) {
        setCreatedProfile(response.data);
        message.success(response.data.message || 'Profile created successfully!');
        form.resetFields();
      } else {
        throw new Error(response.data?.message || 'Failed to create profile');
      }
    } catch (err) {
      console.error('Profile creation error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create profile';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Reset to create another profile
  const handleCreateAnother = () => {
    setCreatedProfile(null);
    setError(null);
    form.resetFields();
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card
        title={<><InstagramOutlined /> Create Miami Profile from Instagram</>}
        style={{ marginBottom: 24 }}
      >
        <Alert
          message="Miami Profile Creator"
          description="Enter an Instagram handle to automatically create a Miami-based profile with AI-generated bio, music genres, and full HD profile picture uploaded to S3."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* Success Result */}
        {createdProfile && (
          <Result
            status="success"
            title="Profile Created Successfully!"
            subTitle={createdProfile.message}
            extra={[
              <Button type="primary" key="another" onClick={handleCreateAnother}>
                Create Another Profile
              </Button>,
              <Button
                key="view"
                onClick={() => window.open(`/profile-detail/${createdProfile.profile?.id}`, '_blank')}
              >
                View Profile
              </Button>
            ]}
          >
            <div style={{ textAlign: 'left', background: '#f5f5f5', padding: 24, borderRadius: 8 }}>
              <Row gutter={16} align="middle">
                <Col>
                  <Avatar size={80} src={createdProfile.profile?.picture}>
                    {createdProfile.profile?.username?.[0]}
                  </Avatar>
                </Col>
                <Col flex={1}>
                  <h3 style={{ margin: 0 }}>{createdProfile.profile?.username}</h3>
                  <p style={{ margin: '4px 0', color: '#666' }}>
                    {createdProfile.profile?.subType} - {createdProfile.profile?.city}
                  </p>
                  <Tag color="success">{createdProfile.profile?.profileType === 'M' ? 'DJ / Artist' : 'Event Manager'}</Tag>
                </Col>
              </Row>

              {createdProfile.profile?.musicGenres?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <strong>Music Genres: </strong>
                  {createdProfile.profile.musicGenres.map((genre, idx) => (
                    <Tag key={idx} color="blue">{genre}</Tag>
                  ))}
                </div>
              )}

              {createdProfile.profile?.bio && (
                <div style={{ marginTop: 12 }}>
                  <strong>Bio:</strong>
                  <p style={{ marginTop: 4, color: '#666' }}>{createdProfile.profile.bio}</p>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <a
                  href={createdProfile.profile?.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Tag icon={<InstagramOutlined />} color="purple">
                    @{createdProfile.instagramHandle}
                  </Tag>
                </a>
              </div>
            </div>
          </Result>
        )}

        {/* Error Display */}
        {error && !createdProfile && (
          <Alert
            message="Profile Creation Failed"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 24 }}
          />
        )}

        {/* Form - Only show when not showing success result */}
        {!createdProfile && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateProfile}
            initialValues={{
              profileType: 'M'
            }}
          >
            <Form.Item
              name="instagramHandle"
              label="Instagram Handle or URL"
              rules={[{ required: true, message: 'Please enter an Instagram handle' }]}
            >
              <Input
                placeholder="@ms_mada_ or https://instagram.com/ms_mada_"
                size="large"
                prefix={<InstagramOutlined />}
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              name="profileType"
              label="Profile Type"
              rules={[{ required: true, message: 'Please select a profile type' }]}
            >
              <Radio.Group size="large" disabled={loading}>
                <Radio.Button value="M">
                  <UserOutlined /> DJ / Artist
                </Radio.Button>
                <Radio.Button value="EM">
                  <TeamOutlined /> Event Manager / Collective
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Divider>Optional Overrides</Divider>

            <Form.Item
              name="pictureUrl"
              label="Picture URL (skip Instagram scraping)"
            >
              <Input
                placeholder="Paste direct image URL if Instagram scraping fails"
                prefix={<PictureOutlined />}
                disabled={loading}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Name (override AI)"
                >
                  <Input
                    placeholder="Leave empty to use AI-generated name"
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="description"
                  label="Bio (override AI)"
                >
                  <Input.TextArea
                    placeholder="Leave empty to use AI-generated bio (280-300 chars)"
                    rows={3}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<UserAddOutlined />}
                loading={loading}
                size="large"
                block
              >
                {loading ? 'Creating Profile...' : 'Create Miami Profile'}
              </Button>
            </Form.Item>

            {loading && (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <Spin size="large" />
                <p style={{ marginTop: 16, color: '#666' }}>
                  Fetching Instagram profile picture, generating AI bio, and uploading to S3...
                </p>
              </div>
            )}
          </Form>
        )}

        {/* Info about what happens */}
        {!createdProfile && !loading && (
          <Alert
            message="What happens when you create a profile:"
            description={
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li>Instagram profile picture is scraped and converted to full HD (or use Picture URL if scraping fails)</li>
                <li>Image is uploaded to S3 (decadanseapp bucket)</li>
                <li>AI generates professional name, bio (280-300 chars), and 2-3 music genres</li>
                <li>Profile is auto-verified and assigned to Miami, US</li>
              </ul>
            }
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>
    </div>
  );
};

export default AIQuickProfileCreator;
